import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

/**
 * ApexCharts Stimulus Controller
 *
 * Renders ApexCharts from a JSON config in data-chart-options-value.
 * Supports all chart types: line, area, bar, donut, radialBar, etc.
 *
 * Usage:
 *   <div data-controller="chart"
 *        data-chart-options-value='{"chart":{"type":"area"},"series":[...]}'
 *        data-chart-height-value="350">
 *   </div>
 */
export default class extends Controller {
  static values = {
    options: Object,
    height: { type: Number, default: 350 },
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    const opts = this.mergeDefaults(this.optionsValue)
    this.chart = new ApexCharts(this.element, opts)
    this.chart.render()
  }

  mergeDefaults(opts) {
    const defaults = {
      chart: {
        height: this.heightValue,
        background: "transparent",
        toolbar: { show: false },
        fontFamily: "Inter, system-ui, sans-serif",
        foreColor: "#9ca3af",
      },
      theme: { mode: "dark" },
      grid: {
        borderColor: "#374151",
        strokeDashArray: 3,
      },
      tooltip: {
        theme: "dark",
        style: { fontSize: "12px" },
      },
      legend: {
        labels: { colors: "#d1d5db" },
      },
      xaxis: {
        axisBorder: { color: "#374151" },
        axisTicks: { color: "#374151" },
      },
    }

    return this.deepMerge(defaults, opts)
  }

  deepMerge(target, source) {
    const result = { ...target }
    for (const key in source) {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        result[key] = this.deepMerge(result[key] || {}, source[key])
      } else {
        result[key] = source[key]
      }
    }
    return result
  }

  optionsValueChanged() {
    if (this.chart) {
      this.chart.destroy()
    }
    this.renderChart()
  }
}
